import { useEffect } from 'react';

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

interface DocumentMetaOptions {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  metaTags?: MetaTag[];
  structuredData?: object[];
}

export function useDocumentMeta(options: DocumentMetaOptions) {
  useEffect(() => {
    // Set title
    if (options.title) {
      document.title = options.title;
    }

    // Remove existing meta tags that we'll be updating
    const existingMetas = document.querySelectorAll('meta[data-dynamic="true"]');
    existingMetas.forEach(meta => meta.remove());

    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
    existingScripts.forEach(script => script.remove());

    // Remove existing canonical
    const existingCanonical = document.querySelector('link[rel="canonical"][data-dynamic="true"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    // Add new meta tags
    const metaTags: MetaTag[] = [];

    if (options.description) {
      metaTags.push({ name: 'description', content: options.description });
    }

    if (options.keywords) {
      metaTags.push({ name: 'keywords', content: options.keywords });
    }

    if (options.ogTitle) {
      metaTags.push({ property: 'og:title', content: options.ogTitle });
    }

    if (options.ogDescription) {
      metaTags.push({ property: 'og:description', content: options.ogDescription });
    }

    if (options.ogImage) {
      metaTags.push({ property: 'og:image', content: options.ogImage });
    }

    if (options.ogUrl) {
      metaTags.push({ property: 'og:url', content: options.ogUrl });
    }

    if (options.twitterCard) {
      metaTags.push({ name: 'twitter:card', content: options.twitterCard });
    }

    if (options.twitterTitle) {
      metaTags.push({ name: 'twitter:title', content: options.twitterTitle });
    }

    if (options.twitterDescription) {
      metaTags.push({ name: 'twitter:description', content: options.twitterDescription });
    }

    if (options.twitterImage) {
      metaTags.push({ name: 'twitter:image', content: options.twitterImage });
    }

    // Add custom meta tags
    if (options.metaTags) {
      metaTags.push(...options.metaTags);
    }

    // Create and append meta tags
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      meta.setAttribute('data-dynamic', 'true');
      
      if (tag.name) {
        meta.setAttribute('name', tag.name);
      }
      
      if (tag.property) {
        meta.setAttribute('property', tag.property);
      }
      
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
    });

    // Add canonical link
    if (options.canonical) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', options.canonical);
      link.setAttribute('data-dynamic', 'true');
      document.head.appendChild(link);
    }

    // Add structured data
    if (options.structuredData && options.structuredData.length > 0) {
      options.structuredData.forEach(data => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-dynamic', 'true');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Cleanup function
    return () => {
      const dynamicMetas = document.querySelectorAll('meta[data-dynamic="true"]');
      dynamicMetas.forEach(meta => meta.remove());

      const dynamicScripts = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
      dynamicScripts.forEach(script => script.remove());

      const dynamicCanonical = document.querySelector('link[rel="canonical"][data-dynamic="true"]');
      if (dynamicCanonical) {
        dynamicCanonical.remove();
      }
    };
  }, [
    options.title,
    options.description,
    options.keywords,
    options.ogTitle,
    options.ogDescription,
    options.ogImage,
    options.ogUrl,
    options.twitterCard,
    options.twitterTitle,
    options.twitterDescription,
    options.twitterImage,
    options.canonical,
    JSON.stringify(options.metaTags),
    JSON.stringify(options.structuredData)
  ]);
}
